import { useCallback, useEffect, useMemo, useState } from 'react';
import { confirm, EmptyState, Icon, showToast, Tabs, Tooltip, Trans, useLatest } from '@orgazone/ui-web';
// ⚠️ LE MÊME CŒUR QUE LE SERVEUR : l'aperçu ne peut pas diverger du fichier produit.
import { DATE_MASK_SUGGESTIONS, defautsDuFormat, forDelivery, formatEntries } from '@orgazone/core-accounting';
import { formatMoney } from '@orgazone/money-format';
import {
  api, type CashMonth, type ChampExport, type ColonneSpec, type ConventionSens, type DayEntry,
  type EntryView, type ExportPasse, type FormatDisponible, type FormatSpec, type PlanComptableView,
  type PennylaneDepot, type PennylaneEtat,
  type PosteKind, type PostePlanComptable,
} from '../api';
import { codeCaisse } from '../caisse';
import { useStoreScope } from '../store-context';
import { REGLEMENTS } from '../reglements';
import { key, t, type Translatable } from '../i18n';
import { currentLang } from '../lang';

/**
 * EXPORT COMPTABLE — la remise au cabinet, et d'abord le plan qui la rend possible.
 *
 * UTILITÉ : c'est l'écran qui transforme des ventes scellées en écritures qu'un comptable peut
 * intégrer. Il commence par le PLAN COMPTABLE parce que rien ne peut être écrit au débit ou au crédit
 * avant lui : les faits sont tous en base (taux de TVA, moyens de règlement, écarts de caisse), c'est
 * la correspondance vers des comptes qui manquait.
 *
 * ⚠️ DEUX ONGLETS SUR TROIS SONT CONSTRUITS (2026-08-22) : le PLAN, puis l'ÉCRITURE qu'il permet de
 * calculer. Les FORMATS de fichier viennent ensuite — et dans cet ordre précis, parce qu'un fichier
 * produit sur un plan incomplet livrerait des écritures fausses qu'un cabinet intégrerait sans les
 * relire. Le troisième onglet dit ce qui lui manque ; il ne simule rien.
 */
export function ExportCompta() {
  // ⚠️ L'ORDRE EST CELUI DE LA MAQUETTE : Générer · Formats paramétrés · Plan comptable. Il suit
  //    l'usage — on vient produire un fichier tous les mois, on règle un format de temps en temps, et
  //    on touche au plan comptable une fois. Mon ordre initial suivait l'ordre de CONSTRUCTION, ce qui
  //    n'intéresse que celui qui l'a construit.
  const [onglet, setOnglet] = useState<'generer' | 'formats' | 'plan'>('generer');
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 18, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 250 }}>
          <h1 style={{ margin: 0, fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink-900)' }}>{t('Export comptable')}</h1>
          <p style={{ margin: '3px 0 0', fontSize: 'var(--t-title)', color: 'var(--ink-500)' }}>
            
            {t('La remise au cabinet — écritures centralisatrices, formats de fichier et envois.')}
          </p>
        </div>
      </div>

      <div style={{ marginBottom: 18 }}>
        <Tabs
          items={[
            { id: 'generer', label: t('Générer') },
            { id: 'formats', label: t('Formats paramétrés') },
            { id: 'plan', label: t('Plan comptable') },
          ]}
          value={onglet}
          onChange={(id) => setOnglet(id as 'generer' | 'formats' | 'plan')}
          panelId="export-compta-panneau"
          label={t('Sections de l’export comptable')}
        />
      </div>

      <div id="export-compta-panneau" role="tabpanel" aria-labelledby={`export-compta-panneau-tab-${onglet}`}>
        {onglet === 'plan' ? <PlanComptable /> : onglet === 'generer' ? <Ecriture /> : <Formats />}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// PLAN COMPTABLE
// ---------------------------------------------------------------------------

/** Ce qu'un poste vaut à l'écran tant que rien n'est enregistré. */
export interface Saisie { account: string; confirm: boolean }

/**
 * CE QUE DEVIENT UNE SAISIE QUAND ON MODIFIE LE COMPTE — elle PERD sa confirmation.
 *
 * ⚠️ SANS ÇA, LA SAISIE HÉRITE D'UNE CONFIRMATION QU'ELLE N'A PAS GAGNÉE. Remplacer un `707100`
 * confirmé par un autre numéro laissait la ligne « confirmée » et l'envoyait telle quelle : le nouveau
 * compte partait au cabinet sans que personne ne l'ait affirmé. C'est exactement ce que la règle
 * « la confirmation est un geste DISTINCT de la saisie » existe pour empêcher — elle valait pour les
 * suggestions, elle vaut tout autant ici.
 *
 * ⚠️ ET LE RETRAIT SE VOIT IMMÉDIATEMENT : la bordure repasse à l'ambre, le bouton à « À confirmer ».
 * Un champ qu'on vient de modifier doit DIRE qu'il attend une décision, pas la supposer acquise.
 *
 * ⚠️ COMPARÉ À L'ESPACE PRÈS : retaper la même valeur, ou ajouter puis retirer un caractère, n'est pas
 * une modification. Faire tomber la confirmation dans ce cas obligerait à recliquer pour rien.
 */
export function saisirCompte(s: Saisie, valeur: string): Saisie {
  const change = valeur.trim() !== s.account.trim();
  return { account: valeur, confirm: change ? false : s.confirm };
}

/**
 * LE PLAN COMPTABLE — un compte par fait de caisse, dicté par le cabinet.
 *
 * ⚠️ LES POSTES VIENNENT DU SERVEUR, ET DES VENTES RÉELLES. L'écran n'en fabrique aucun : un commerce
 * qui vend à 2,1 % voit apparaître la ligne, un commerce qui n'encaisse jamais de chèque ne se voit
 * pas réclamer un compte 511100. Une liste écrite ici aurait à choisir entre demander l'inutile et
 * oublier le nécessaire.
 *
 * ⚠️ UNE LIGNE S'ENREGISTRE SEULE (décision utilisateur 2026-08-22). Le bouton de ligne était une
 * simple marque locale, et un enregistrement global vivait en haut de page : personne ne pouvait
 * deviner que cliquer « Confirmer » n'écrivait rien. Un bouton posé sur une ligne DOIT agir sur cette
 * ligne — c'est la lecture naturelle, et c'était la nôtre qui était fausse. Corollaire heureux :
 * valider et enregistrer deviennent UN SEUL geste au lieu de deux.
 */
function PlanComptable() {
  const scope = useStoreScope();
  const gln = scope.view?.active?.gln ?? null;
  const [vue, setVue] = useState<PlanComptableView | null>(null);
  const [saisies, setSaisies] = useState<Record<string, Saisie>>({});
  const [etats, setEtats] = useState<Record<string, EtatLigne>>({});
  const [erreur, setErreur] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // ⚠️ SEULE LA DERNIÈRE LECTURE ÉCRIT L'ÉCRAN : le plan de l'ancien magasin, revenu après celui du nouveau,
  //    remplirait les saisies de comptes que ce magasin n'a pas — et le prochain « Confirmer » les y écrirait.
  const latest = useLatest();
  const load = useCallback(() => {
    if (!gln) { setLoading(false); return; }
    const isLatest = latest();
    setLoading(true);
    setErreur(null);
    api.accountingPlan()
      .then((v) => { if (!isLatest()) return; setVue(v); setSaisies(depuisLaVue(v)); setEtats({}); setLoading(false); })
      // ⚠️ LE MESSAGE RÉEL, et l'état de succès NON appliqué : un plan qu'on n'a pas pu lire ne doit
      //    jamais ressembler à un plan vide — l'un se corrige, l'autre se remplit.
      .catch((e: unknown) => { if (!isLatest()) return; setErreur(e instanceof Error ? e.message : String(e)); setLoading(false); });
  }, [gln, latest]);

  useEffect(load, [load]);

  const postes = useMemo(() => vue?.postes ?? [], [vue]);

  /**
   * VALIDE UN COMPTE — et l'écrit, dans le même geste.
   *
   * ⚠️ LE SUCCÈS SE RECOPIE LOCALEMENT PLUTÔT QUE DE RECHARGER LE PLAN. Recharger effacerait les
   * saisies en cours des AUTRES lignes : on remplit volontiers cinq comptes avant d'en valider un, et
   * perdre les quatre autres à chaque clic rendrait l'écran inutilisable.
   *
   * ⚠️ L'ÉCHEC RESTE SUR SA LIGNE, avec le message du serveur. Un refus porte toujours sur un compte
   * précis (« 70 » n'est pas un compte, c'est une classe) : le remonter en haut de page obligerait à
   * chercher lequel des quinze champs il désigne.
   */
  const validate = async (p: PostePlanComptable, confirmer: boolean) => {
    const s = saisies[p.key];
    if (!s || etats[p.key]?.phase === 'envoi') return;
    const compte = s.account.trim();
    setEtats((e) => ({ ...e, [p.key]: { phase: 'envoi' } }));
    try {
      await api.writeMasterData(
        'accounting_map',
        [{ key: p.key, kind: p.kind, account: compte, label: p.label, confirm: confirmer }],
        // ⚠️ CLÉ D’IDEMPOTENCE, jamais un texte : le serveur s’en sert pour reconnaître qu’il a
        //    DÉJÀ écrit ce poste. Traduite, elle en vaudrait deux.
        `plan-comptable:${p.key}`,
      );
      setSaisies((prev) => ({ ...prev, [p.key]: { account: compte, confirm: confirmer } }));
      setVue((v) => (v ? {
        ...v,
        postes: v.postes.map((x) => (x.key === p.key
          ? { ...x, account: compte, etat: confirmer ? 'confirme' : 'suggere' }
          : x)),
      } : v));
      setEtats((e) => { const n = { ...e }; delete n[p.key]; return n; });
    } catch (e) {
      setEtats((prev) => ({
        ...prev,
        [p.key]: { phase: 'echec', message: e instanceof Error ? e.message : String(e) },
      }));
    }
  };

  if (!gln) {
    return <EmptyState icon="store" title={t('Aucun magasin actif')} note={t('Choisissez le magasin à administrer pour voir son plan comptable.')} />;
  }
  if (erreur) return <EmptyState icon="alert" title={t('Plan comptable illisible')} note={erreur} />;
  if (loading) return <EmptyState icon="clock" title={t('Lecture du plan…')} note={t('Les postes de ce magasin sont en cours de lecture.')} />;
  if (!vue) return <EmptyState icon="alert" title={t('Plan comptable illisible')} note={t('Le serveur n’a rien rendu pour ce magasin.')} />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Verrou vue={vue} />

      {GROUPES.map((g) => {
        const rows = postes.filter((p) => p.kind === g.kind);
        if (!rows.length) return null;
        return (
          <div key={g.kind} className="dcard" style={{ overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderBottom: '1px solid var(--ink-200)', flexWrap: 'wrap' }}>
              <span style={{ flex: 1, minWidth: 170, fontSize: 'var(--t-strong)', fontWeight: 700, color: 'var(--ink-900)' }}>{t(g.heading)}</span>
              <span style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)', flexShrink: 0 }}>{t(g.sens)}</span>
            </div>
            {rows.map((p) => (
              <LignePoste
                key={p.key}
                p={p}
                s={saisies[p.key] ?? { account: p.account, confirm: p.etat === 'confirme' }}
                etat={etats[p.key]}
                onChange={(s) => setSaisies((prev) => ({ ...prev, [p.key]: s }))}
                onValidate={(confirmer) => void validate(p, confirmer)}
              />
            ))}
          </div>
        );
      })}

      <div style={{ padding: '14px 17px', borderRadius: 'var(--r-md)', background: 'var(--ink-50)', border: '1px solid var(--ink-200)', fontSize: 'var(--t-body)', color: 'var(--ink-600)', lineHeight: 1.6 }}>
        <Trans k='<1>Ces numéros ne s’inventent pas</1> — ils viennent du plan comptable du cabinet, qui les dicte. Le back-office les stocke et les applique ; il ne décide d’aucun compte à votre place. Les valeurs pré-remplies sont celles du plan comptable général français : elles restent des <2>propositions</2> tant que personne ne les a validées, et l’export reste fermé jusque-là. <3>Valider une ligne l’enregistre</3> — il n’y a rien d’autre à faire ensuite.' parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} /></div>
    </div>
  );
}

/** Où en est l'écriture d'une ligne. Absent = rien en cours, rien à signaler. */
type EtatLigne = { phase: 'envoi' } | { phase: 'echec'; message: string };

/**
 * CE QUI FERME L'EXPORT — l'encart qui dit exactement ce qui manque.
 *
 * ⚠️ IL COMPTE LES SUGGESTIONS COMME MANQUANTES, et c'est tout le sujet. Un champ pré-rempli à
 * `707100` ressemble trait pour trait à un compte dicté par le cabinet ; sans cette distinction, un
 * plan jamais relu s'exporterait en se présentant comme validé. La règle vit au serveur — l'écran ne
 * fait que la rendre lisible.
 *
 * ⚠️ IL COMPTE SUR CE QUI EST ENREGISTRÉ, jamais sur ce qui est tapé. Une valeur frappée mais non
 * validée n'existe pas pour l'export : l'annoncer complète ferait promettre à l'écran un état que la
 * base n'a pas (§23.0).
 */
function Verrou({ vue }: { vue: PlanComptableView }) {
  const vides = vue.postes.filter((p) => p.etat === 'vide').length;
  const aValider = vue.postes.filter((p) => p.etat === 'suggere').length;
  const complet = vides === 0 && aValider === 0;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '13px 16px', borderRadius: 'var(--r-md)',
      background: complet ? 'var(--teal-100)' : 'var(--amber-bg)',
      border: `1px solid ${complet ? 'var(--teal-500)' : 'var(--amber)'}`, flexWrap: 'wrap',
    }}>
      <span style={{ color: complet ? 'var(--teal-800)' : 'var(--amber-fg)', display: 'flex', flexShrink: 0 }}>
        <Icon name={complet ? 'check' : 'alert'} size={17} stroke={2.2} />
      </span>
      <span style={{ flex: 1, minWidth: 240, fontSize: 'var(--t-body)', lineHeight: 1.55, color: complet ? 'var(--teal-800)' : 'var(--amber-fg)' }}>
        {complet ? (
          <><Trans k='<1>Plan complet</1> — chaque fait de caisse a son compte, et chacun a été validé.' parts={[(x) => <strong>{x}</strong>]} /></>
        ) : (
          <>
            <strong>{t('L’export reste fermé')}</strong> —{' '}
            {vides > 0 ? t('{n} compte non renseigné', { n: vides }) : null}
            {vides > 0 && aValider > 0 ? ', ' : null}
            {aValider > 0 ? t('{n} compte à valider', { n: aValider }) : null}.{' '}
            
            {t('Un export partiel produirait des écritures fausses qu’un cabinet intégrerait.')}
          </>
        )}
        {!vue.aDesVentes ? (
          <span style={{ display: 'block', marginTop: 4, opacity: 0.85 }}>
            
            {t('Ce magasin n’a encore aucune vente scellée : les postes ci-dessous viennent de son référentiel. D’autres apparaîtront d’eux-mêmes au premier taux ou moyen de paiement employé.')}
          </span>
        ) : null}
      </span>
    </div>
  );
}

/**
 * Une ligne du plan : le fait de caisse, son compte, et le geste qui l'engage.
 *
 * ⚠️ UN SEUL GESTE, QUI VALIDE **ET** ENREGISTRE. Séparer les deux avait produit exactement le
 * malentendu qu'on corrige : un bouton de ligne qui n'écrivait rien, et un enregistrement global
 * ailleurs. Personne ne peut deviner ça — et le deviner mal, ici, c'est croire un plan enregistré
 * alors qu'il ne l'est pas.
 *
 * ⚠️ « COMPTE VALIDÉ » N'EST PAS UN BOUTON, c'est un état. Le rendre cliquable demanderait d'inventer
 * une action pour un clic — dévalider ? réenregistrer ? — là où le geste naturel est de corriger le
 * champ, qui rouvre la ligne de lui-même. Un bouton dont on ne sait pas dire ce qu'il fait est un
 * bouton qui fait semblant (§23.0).
 */
function LignePoste({ p, s, etat, onChange, onValidate }: {
  p: PostePlanComptable; s: Saisie; etat: EtatLigne | undefined;
  onChange: (s: Saisie) => void; onValidate: (confirmer: boolean) => void;
}) {
  const vide = s.account.trim() === '';
  const envoi = etat?.phase === 'envoi';
  // ⚠️ TROIS ÉTATS, DEUX COULEURS, ET SURTOUT PAS DE GRIS NEUTRE. Un compte validé portait la bordure
  //    la plus discrète de la charte, entouré de voisins en ambre : il avait l'air VERROUILLÉ alors
  //    qu'il reste saisissable (remarque utilisateur 2026-08-22). Le turquoise dit « validé »,
  //    l'ambre dit « il manque une décision » — et la bordure répète ce que dit la colonne d'à côté,
  //    parce qu'on relit quinze lignes d'un coup d'œil et pas un bouton à la fois.
  const bordure = etat?.phase === 'echec' ? 'var(--rose)' : vide || !s.confirm ? 'var(--amber)' : 'var(--teal-500)';
  const icone = p.kind === 'payment' ? REGLEMENTS[p.discriminant]?.icon : undefined;
  const tone = p.kind === 'payment' ? REGLEMENTS[p.discriminant]?.tone : undefined;

  return (
    <div style={{ borderBottom: '1px solid var(--ink-100)' }}>
      <div className="oz-plan-ligne" style={{ padding: '10px 18px', alignItems: 'center' }}>
        <span style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 9 }}>
          {icone ? <span style={{ display: 'flex', color: tone, flexShrink: 0 }}><Icon name={icone} size={15} stroke={1.9} /></span> : null}
          <span style={{ minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {p.label}
            </span>
            {/* ⚠️ « VU DANS VOS VENTES » N'EST PAS UN ORNEMENT : il distingue un poste que ce commerce
                emploie vraiment d'un poste que son référentiel annonce. Le premier bloque un export réel. */}
            {p.observe ? (
              <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-400)', marginTop: 1 }}>{t('Vu dans vos ventes')}</span>
            ) : null}
          </span>
        </span>

        <input
          value={s.account}
          onChange={(e) => onChange(saisirCompte(s, e.target.value))}
          placeholder={p.suggestion || t('Compte')}
          aria-label={p.label}
          disabled={envoi}
          style={{
            height: 38, padding: '0 11px', border: `1px solid ${bordure}`, borderRadius: 'var(--r-ctrl)',
            background: 'var(--paper)', color: 'var(--ink-900)', fontFamily: 'var(--font-mono)',
            fontSize: 'var(--t-lead)', outline: 'none', width: '100%',
          }}
        />

        {s.confirm && !envoi ? (
          <span style={{
            height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, width: '100%',
            fontSize: 'var(--t-dense)', fontWeight: 700, color: 'var(--teal-800)',
          }}>
            <Icon name="check" size={13} stroke={2.2} />
            
            {t('Compte validé')}
          </span>
        ) : (
          <button
            type="button"
            onClick={() => onValidate(true)}
            disabled={vide || envoi}
            className="oz-integ-btn"
            style={{
              height: 38, padding: '0 11px', borderRadius: 'var(--r-ctrl)', width: '100%',
              border: '1px solid var(--teal-500)', background: 'var(--teal-100)', color: 'var(--teal-800)',
              fontSize: 'var(--t-dense)', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              cursor: vide || envoi ? 'not-allowed' : 'pointer', opacity: vide || envoi ? 0.5 : 1,
              font: 'inherit', fontFamily: 'inherit',
            }}
          >
            {envoi ? t('Enregistrement…') : t('Valider ce compte')}
          </button>
        )}
      </div>

      {/* ⚠️ LE REFUS RESTE SUR SA LIGNE, avec le message du serveur : il porte toujours sur un compte
          précis, et le remonter en haut de page obligerait à chercher lequel des quinze il désigne. */}
      {etat?.phase === 'echec' ? (
        <div style={{ padding: '0 18px 10px', fontSize: 'var(--t-note)', color: 'var(--rose-fg)', lineHeight: 1.5 }}>
          {t('Non enregistré — {message}', { message: etat.message })}
        </div>
      ) : null}
    </div>
  );
}

/** Les familles de postes, dans l'ordre où un comptable les relit. */
const GROUPES: readonly { kind: PosteKind; heading: Translatable; sens: Translatable }[] = [
  { kind: 'journal', heading: key('Journal'), sens: key('Code du journal') },
  { kind: 'vat_sales', heading: key('Comptes de vente'), sens: key('Au crédit') },
  { kind: 'vat_collected', heading: key('TVA collectée'), sens: key('Au crédit') },
  { kind: 'payment', heading: key('Comptes de règlement'), sens: key('Au débit') },
  { kind: 'variance', heading: key('Cas particuliers'), sens: key('Selon le sens') },
];

/** L'état de saisie initial : ce que le serveur a rendu, tel quel. */
function depuisLaVue(v: PlanComptableView): Record<string, Saisie> {
  const out: Record<string, Saisie> = {};
  for (const p of v.postes) out[p.key] = { account: p.account, confirm: p.etat === 'confirme' };
  return out;
}


// ---------------------------------------------------------------------------
// CE QUI RESTE À CONSTRUIRE
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// LES ÉCRITURES DE LA PÉRIODE — UNE PAR JOURNÉE
// ---------------------------------------------------------------------------

/**
 * LES ÉCRITURES D'UN MOIS — une par journée fiscale, comme la caisse les produit.
 *
 * ⚠️ AU JOUR, JAMAIS AU MOIS (décision utilisateur 2026-08-23). La première version agrégeait le mois
 * en une seule écriture ; c'était une erreur. Un mois n'est qu'un CUMUL de journées, et agréger
 * d'abord fait perdre la seule granularité qui existe en caisse — celle que le Z scelle, celle qu'un
 * comptable rapproche d'un relevé bancaire, celle qui permet de dire QUEL jour ne tombe pas juste.
 *
 * ⚠️ LA LISTE MONTRE D'ABORD LES JOURNÉES, PAS LEURS LIGNES. Un mois chargé compte trente journées de
 * huit lignes : les dérouler toutes donnerait un mur de deux cent cinquante lignes où la seule
 * question qui compte — « est-ce que tout tombe juste ? » — serait invisible. On répond à celle-là en
 * premier, et le détail s'ouvre au clic.
 *
 * ⚠️ AUCUN BOUTON D'EXPORT ICI. Le formateur paramétrable n'existe pas encore ; en poser un qui ne
 * produirait rien serait l'interdit n°6 du §23.0. Ce qui n'est pas branché se dit.
 */
function Ecriture() {
  const scope = useStoreScope();
  const gln = scope.view?.active?.gln ?? null;
  const [month, setMois] = useState<CashMonth[]>([]);
  const [choisi, setChoisi] = useState<string | null>(null);
  const [vue, setVue] = useState<EntryView | null>(null);
  const [ouvert, setOuvert] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [formats, setFormats] = useState<FormatDisponible[]>([]);
  const [format, setFormat] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [envoiFec, setEnvoiFec] = useState(false);
  // ⚠️ Un COMPTEUR plutôt qu'un rappel de fonction : l'historique est un composant enfant, et lui
  //    passer une fonction qui change à chaque rendu le ferait recharger en boucle.
  const [rafraichirHistorique, setRafraichirHistorique] = useState(0);

  // ⚠️ LES MOIS OFFERTS SONT CEUX QUI ONT DES VENTES, lus au relevé mensuel des clôtures — jamais une
  //    liste de douze mois glissants dont la moitié serait vide. On ne propose pas d'exporter le néant.
  useEffect(() => {
    if (!gln) { setLoading(false); return; }
    let vivant = true;
    api.cashMonths()
      .then((m) => {
        if (!vivant) return;
        setMois(m.months);
        setChoisi((c) => c ?? (m.months[0] ? moisIso(m.months[0].ym) : null));
        if (!m.months.length) setLoading(false);
      })
      .catch((e: unknown) => { if (vivant) { setErreur(e instanceof Error ? e.message : String(e)); setLoading(false); } });
    return () => { vivant = false; };
  }, [gln]);

  useEffect(() => {
    if (!gln) return;
    let vivant = true;
    api.accountingFormats()
      .then((f) => { if (vivant) { setFormats(f.formats); setFormat((c) => c ?? f.formats[0]?.code ?? null); } })
      .catch(() => { if (vivant) setFormats([]); });
    return () => { vivant = false; };
  }, [gln]);

  useEffect(() => {
    if (!choisi) return;
    let vivant = true;
    setLoading(true);
    setErreur(null);
    setOuvert(null);
    api.accountingEntry(choisi)
      .then((v) => { if (vivant) { setVue(v); setLoading(false); } })
      // ⚠️ Le message RÉEL, et aucun état de succès : des écritures qu'on n'a pas pu lire ne doivent
      //    jamais ressembler à un mois sans vente.
      .catch((e: unknown) => { if (vivant) { setErreur(e instanceof Error ? e.message : String(e)); setLoading(false); } });
    return () => { vivant = false; };
  }, [choisi]);

  if (!gln) return <EmptyState icon="store" title={t('Aucun magasin actif')} note={t('Choisissez le magasin à administrer pour voir ses écritures comptables.')} />;
  if (erreur) return <EmptyState icon="alert" title={t('Écritures illisibles')} note={erreur} />;
  if (!month.length && !loading) {
    return <EmptyState icon="receipt" title={t('Aucun mois à exporter')} note={t('Ce magasin n’a encore aucune vente scellée : il n’y a rien à écrire.')} />;
  }
  if (loading || !vue) return <EmptyState icon="clock" title={t('Calcul des écritures…')} note={t('Les journées du mois sont en cours d’agrégation.')} />;

  /**
   * Produit le fichier et le remet au navigateur.
   *
   * ⚠️ LE TÉLÉCHARGEMENT NE SE DÉCLENCHE QU'APRÈS SUCCÈS. Poser le lien d'abord ferait enregistrer un
   * message d'erreur JSON sur le disque sous le nom d'un fichier comptable — le défaut classique des
   * téléchargements, et celui qu'on découvre chez le cabinet.
   * ⚠️ LE NOM VIENT DU SERVEUR : un cabinet reçoit les fichiers de plusieurs clients, et « export.csv »
   * dans un dossier de téléchargements ne désigne rien.
   */
  /** Produit le FEC réglementaire — même contrat que l'export : téléchargement APRÈS succès seulement.
   *  `exercice` = l'année ENTIÈRE du mois choisi (janvier → décembre) : UN fichier, EcritureNum
   *  continue, nommé à la date de clôture — la forme qu'une réquisition demande. */
  async function exporterFec(exercice: boolean) {
    if (!choisi || envoiFec) return;
    setEnvoiFec(true);
    try {
      const du = exercice ? `${choisi.slice(0, 4)}-01` : choisi;
      const au = exercice ? `${choisi.slice(0, 4)}-12` : choisi;
      const { blob, filename, headers } = await api.accountingFecFile(du, au);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // ⚠️ LE FEC EST UN FICHIER NORMÉ : son nom ne dépend pas de la langue de celui qui l’exporte.
      a.download = filename ?? `FEC-${choisi}.txt`;
      a.click();
      URL.revokeObjectURL(url);
      const remplaces = Number(headers.get('x-orgazone-remplaces') ?? 0);
      showToast(
        remplaces > 0
          ? t('FEC produit — {n} caractère hors ISO 8859-15 remplacé par « ? »', { n: remplaces })
          : fecLignes(headers),
        remplaces > 0 ? 'warn' : 'info', 6000,
      );
      setRafraichirHistorique((n) => n + 1);
    } catch (e) {
      showToast(t('FEC refusé : {v}', { v: e instanceof Error ? e.message : String(e) }), 'err', 9000);
    } finally {
      setEnvoiFec(false);
    }
  }

  async function exporter() {
    if (!choisi || !format || envoi) return;
    setEnvoi(true);
    try {
      const { blob, filename, headers } = await api.accountingExportFile(choisi, format);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // ⚠️ NOM DE FICHIER, jamais un texte — convention de tous les exports du back-office.
      a.download = filename ?? `compta-${choisi}.csv`;
      a.click();
      URL.revokeObjectURL(url);

      // ⚠️ LA PERTE D'ENCODAGE SE DIT. Un caractère absent de Windows-1252 part en « ? » ; le taire
      //    ferait livrer un libellé mutilé qu'on ne découvre que dans le grand livre.
      const remplaces = Number(headers.get('x-orgazone-remplaces') ?? 0);
      showToast(
        remplaces > 0
          ? t('Fichier produit — {n} caractère non représentable remplacé par « ? »', { n: remplaces })
          : fichierLignes(headers),
        remplaces > 0 ? 'warn' : 'info', 6000,
      );
      setRafraichirHistorique((n) => n + 1);
    } catch (e) {
      showToast(t('Export refusé : {v}', { v: e instanceof Error ? e.message : String(e) }), 'err', 9000);
    } finally {
      setEnvoi(false);
    }
  }

  const journal = vue.days[0]?.journal ?? '';
  const pretAExporter = vue.planComplet && vue.balanced;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Barre de filtres — relevé maquette l.10314 : canal · période · format · bouton, sur une ligne. */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <span style={{ flexShrink: 0 }}>
          <EtiquetteFiltre>{t('Canal de remise')}</EtiquetteFiltre>
          <Canal />
        </span>
        <label style={{ flex: '0 1 190px', minWidth: 160 }}>
          <EtiquetteFiltre>{t('Période')}</EtiquetteFiltre>
          <select
            value={choisi ?? ''} onChange={(ev) => setChoisi(ev.target.value)} aria-label={t('Mois à exporter')}
            style={selectFiltre}
          >
            {month.map((m) => <option key={m.ym} value={moisIso(m.ym)}>{moisEnClair(m.ym)}</option>)}
          </select>
        </label>
        <label style={{ flex: 1, minWidth: 200 }}>
          <EtiquetteFiltre>{t('Format paramétré')}</EtiquetteFiltre>
          <select
            value={format ?? ''} onChange={(ev) => setFormat(ev.target.value)} aria-label={t('Format d’export')}
            style={selectFiltre}
          >
            {formats.filter((f) => !f.illisible).map((f) => <option key={f.code} value={f.code}>{f.label}</option>)}
          </select>
        </label>
        <button
          type="button" onClick={() => void exporter()} disabled={!pretAExporter || !format || envoi}
          className="oz-integ-btn"
          style={{
            height: 40, padding: '0 17px', border: 'none', borderRadius: 'var(--r-sm)',
            background: 'var(--teal-500)', color: '#fff', fontSize: 'var(--t-title)', fontWeight: 700,
            flexShrink: 0, whiteSpace: 'nowrap', font: 'inherit', fontFamily: 'inherit',
            cursor: !pretAExporter || envoi ? 'not-allowed' : 'pointer',
            opacity: !pretAExporter || envoi ? 0.5 : 1,
          }}
        >
          {envoi ? t('Production…') : t('Produire le fichier')}
        </button>
      </div>

      {/* ⚠️ LA BASCULE SE DIT quand elle n'est pas à minuit : sans ça, une journée qui commence à 02h00
          passe pour un décalage inexpliqué au moment où l'on compare une écriture à un relevé. */}
      {vue.dayCutoffMinutes > 0 ? (
        <div style={{ padding: '11px 15px', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)', border: '1px solid var(--ink-200)', fontSize: 'var(--t-body)', color: 'var(--ink-600)', lineHeight: 1.6 }}>
          <Trans k='Journées à bascule <1>{v}</1> — une vente d’avant cette heure appartient à la veille, comme sur le Z.' vars={{ v: heureDeBascule(vue.dayCutoffMinutes) }} parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} /></div>
      ) : null}

      {/* Encart des formats — relevé maquette l.10352. Il DIT que la liste appartient au client. */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '11px 15px', borderRadius: 'var(--r-sm)', background: 'var(--teal-100)', border: '1px solid var(--teal-300)' }}>
        <span style={{ display: 'flex', flexShrink: 0, color: 'var(--teal-800)', paddingTop: 1 }}>
          <Icon name="edit" size={15} stroke={2} />
        </span>
        <span style={{ fontSize: 'var(--t-body)', color: 'var(--teal-800)', lineHeight: 1.6 }}>
          <Trans k='Cette liste est <1>la vôtre, pas un catalogue figé</1> : chaque format est entièrement paramétrable — colonnes, libellés, séparateur, encodage — et se gère à la demande avec le cabinet. Un format se crée ou se modifie dans l’onglet <2>Formats paramétrés</2>.' parts={[(x) => <strong>{x}</strong>, (x) => <strong>{x}</strong>]} /></span>
      </div>

      {/* FEC réglementaire — un CANAL à part, jamais un format de la liste : normalisé, non
          paramétrable (art. A47 A-1 LPF), l'étalon du moteur et le fichier d'un contrôle fiscal. */}
      <div className="dcard" style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '13px 16px', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ fontSize: 'var(--t-title)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('FEC réglementaire')}</div>
          <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)', lineHeight: 1.6, marginTop: 2 }}>
            <Trans k='Le fichier des écritures comptables (art. A47 A-1 LPF) : 18 colonnes normalisées, ISO 8859-15 — rien ne s’y règle. Une <1>réquisition porte sur l’exercice entier</1> : « Exercice » produit UN fichier janvier → décembre, numéroté en continu, nommé à la date de clôture. Le fichier d’un seul mois sert le cabinet. Une journée encore ouverte part sans date de validation.' parts={[(x) => <strong>{x}</strong>]} /></div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          {([[t('Mois choisi'), false], [t('Exercice {v}', { v: choisi?.slice(0, 4) ?? '' }), true]] as const).map(([l, ex]) => (
            <button
              key={l}
              type="button" onClick={() => void exporterFec(ex)} disabled={!pretAExporter || envoiFec}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, height: 38, padding: '0 14px',
                borderRadius: 'var(--r-sm)', border: '1px solid var(--ink-300)', background: 'var(--paper)',
                color: 'var(--ink-900)', fontSize: 'var(--t-lead)', fontWeight: 600, cursor: !pretAExporter || envoiFec ? 'default' : 'pointer',
                opacity: !pretAExporter || envoiFec ? 0.5 : 1,
              }}
            >
              <Icon name="download" size={14} stroke={2} />
              {envoiFec ? t('Production…') : l}
            </button>
          ))}
        </div>
      </div>

      <BlocPennylane choisi={choisi} ready={pretAExporter} />

      <Provenance vue={vue} />

      <div className="dcard" style={{ overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderBottom: '1px solid var(--ink-200)', flexWrap: 'wrap' }}>
          <span style={{ flex: 1, minWidth: 170, fontSize: 'var(--t-strong)', fontWeight: 700, color: 'var(--ink-900)' }}>
            {t('Écritures du mois — {n} journée', { n: vue.days.length })}
          </span>
          <span style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)', flexShrink: 0 }}>
            {journal ? t('Journal {journal}', { journal }) : t('Journal non renseigné')}
          </span>
        </div>

        {vue.days.length === 0 ? (
          <EmptyState icon="receipt" title={t('Aucune journée à écrire')} note={t('Ce mois n’a produit ni vente, ni règlement, ni écart de caisse.')} />
        ) : (
          <>
            <div className="oz-jour-ligne" style={{ padding: '10px 18px', background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)', fontSize: 'var(--t-sub)', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>
              <div>{t('Journée')}</div><div>{t('Lignes')}</div><div style={{ textAlign: 'right' }}>{t('Débit')}</div><div style={{ textAlign: 'right' }}>{t('Crédit')}</div><div />
            </div>
            {vue.days.map((d) => (
              <JourneeEcriture
                key={d.provenance.month}
                d={d}
                ouvert={ouvert === d.provenance.month}
                onBasculer={() => setOuvert(ouvert === d.provenance.month ? null : d.provenance.month)}
              />
            ))}
            <div className="oz-jour-ligne" style={{ padding: '13px 18px', alignItems: 'center', background: vue.balanced ? 'var(--teal-100)' : 'var(--rose-bg)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                <span style={{ display: 'flex', color: vue.balanced ? 'var(--teal-800)' : 'var(--rose-fg)', flexShrink: 0 }}>
                  <Icon name={vue.balanced ? 'check' : 'alert'} size={15} stroke={2.3} />
                </span>
                <span style={{ fontSize: 'var(--t-body)', fontWeight: 700, color: vue.balanced ? 'var(--teal-800)' : 'var(--rose-fg)' }}>
                  {vue.balanced ? t('Toutes équilibrées') : t('Des journées en défaut')}
                </span>
              </div>
              {/* ⚠️ CUMUL DU MOIS, DÉRIVÉ DES JOURNÉES et jamais recalculé à part : deux chemins de
                  calcul finiraient par donner deux totaux du même mois. */}
              <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-600)' }}>{t('Cumul du mois')}</div>
              <div className="tnum" style={{ textAlign: 'right', fontSize: 'var(--t-control)', fontWeight: 800, color: 'var(--ink-900)' }}>{formatMoney(vue.totalDebitCents, { thousandsSep: ' ' })}</div>
              <div className="tnum" style={{ textAlign: 'right', fontSize: 'var(--t-control)', fontWeight: 800, color: 'var(--ink-900)' }}>{formatMoney(vue.totalCreditCents, { thousandsSep: ' ' })}</div>
              <div />
            </div>
          </>
        )}

        <div style={{ padding: '14px 18px', background: 'var(--ink-50)', borderTop: '1px solid var(--ink-200)', fontSize: 'var(--t-body)', color: 'var(--ink-600)', lineHeight: 1.6 }}>
          <Trans k='<1>Une écriture par journée</1>, pas une ligne par ticket : chaque jour est agrégé par taux et par mode de règlement, et s’équilibre pour lui-même. Le mois n’est que leur cumul. Le détail des ventes reste au <2>journal des ventes</2>, qui est la pièce justificative.' parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} /></div>
      </div>

      {choisi ? <Historique month={choisi} rafraichir={rafraichirHistorique} /> : null}
    </div>
  );
}

/** Une journée dans la liste : son bilan d'un coup d'œil, ses lignes au clic. */
function JourneeEcriture({ d, ouvert, onBasculer }: { d: DayEntry; ouvert: boolean; onBasculer: () => void }) {
  const enDefaut = !d.balanced || d.problems.length > 0;
  return (
    <div>
      <button
        type="button" onClick={onBasculer} className="oz-integ-ligne oz-jour-ligne"
        style={{
          padding: '0 18px', minHeight: 44, alignItems: 'center', width: '100%',
          border: 'none', borderBottom: '1px solid var(--ink-100)',
          background: ouvert ? 'var(--ink-50)' : 'transparent', textAlign: 'left', cursor: 'pointer', font: 'inherit',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-900)' }}>
          <span style={{ display: 'flex', color: enDefaut ? 'var(--rose-fg)' : 'var(--teal-700)', flexShrink: 0 }}>
            <Icon name={enDefaut ? 'alert' : 'check'} size={14} stroke={2.2} />
          </span>
          {jourEnClair(d.provenance.month)}
        </span>
        <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>{d.lines.length}</span>
        <span className="tnum" style={{ textAlign: 'right', fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--ink-900)' }}>{formatMoney(d.totalDebitCents, { thousandsSep: ' ' })}</span>
        <span className="tnum" style={{ textAlign: 'right', fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--ink-900)' }}>{formatMoney(d.totalCreditCents, { thousandsSep: ' ' })}</span>
        <span style={{ display: 'flex', justifyContent: 'flex-end', color: 'var(--ink-300)' }}>
          <Icon name={ouvert ? 'chevronD' : 'chevronR'} size={14} stroke={2.2} />
        </span>
      </button>

      {ouvert ? (
        <div style={{ background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)', padding: '4px 0 10px' }}>
          {d.problems.map((pb) => (
            <div key={`${pb.kind}-${pb.key ?? ''}`} style={{ padding: '8px 18px', fontSize: 'var(--t-dense)', color: 'var(--rose-fg)', lineHeight: 1.5 }}>{pb.message}</div>
          ))}
          {/* ⚠️ LES CAISSES SANS CLÔTURE SE NOMMENT PAR JOURNÉE : c'est cette journée-là qui est
              incomplète, et savoir laquelle relancer est toute l'information utile. */}
          {d.provenance.openRegisters.length > 0 ? (
            <div style={{ padding: '8px 18px', fontSize: 'var(--t-dense)', color: 'var(--amber-fg)', lineHeight: 1.5 }}>
              <Trans k='{n} caisse sans clôture ce jour-là : <1>{v}</1> — des pièces peuvent encore arriver sur cette journée.' vars={{ n: d.provenance.openRegisters.length, v: d.provenance.openRegisters.map(codeCaisse).join(', ') }} parts={[(x) => <strong>{x}</strong>]} /></div>
          ) : null}
          {d.lines.map((l, i) => (
            <div key={`${l.origin}-${i}`} className="oz-ecr-ligne" style={{ padding: '0 18px', minHeight: 38, alignItems: 'center', fontSize: 'var(--t-lead)' }}>
              <div style={{ minWidth: 0, fontFamily: 'var(--font-mono)', fontSize: 'var(--t-dense)', fontWeight: 700, color: 'var(--ink-700)' }}>{l.account}</div>
              <div style={{ minWidth: 0, color: 'var(--ink-800)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.label}</div>
              <div className="tnum" style={{ minWidth: 0, textAlign: 'right', fontWeight: l.debitCents ? 700 : 400, color: 'var(--ink-900)' }}>
                {l.debitCents ? formatMoney(l.debitCents, { thousandsSep: ' ' }) : '—'}
              </div>
              <div className="tnum" style={{ minWidth: 0, textAlign: 'right', fontWeight: l.creditCents ? 700 : 400, color: 'var(--ink-900)' }}>
                {l.creditCents ? formatMoney(l.creditCents, { thousandsSep: ' ' }) : '—'}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * CE QUI QUALIFIE LA PÉRIODE — scellée ou non, plan complet ou non.
 *
 * ⚠️ UN MOIS OUVERT N'EMPÊCHE PAS L'EXPORT (décision produit) : une caisse en panne le 31 bloquerait
 * sinon la comptabilité d'un magasin entier. Le danger n'est pas d'exporter, c'est qu'un cabinet
 * intègre un mois incomplet SANS LE SAVOIR — d'où cet encart.
 */
/**
 * LE CANAL API — Pennylane, écritures déposées en BROUILLON (cadrage Export-Comptable §5.7).
 * L'état vient du serveur (§23.0 : « non configuré » ≠ « en panne » ≠ « prêt ») ; la clé se POSE et
 * ne se relit jamais ; le compte rendu du dépôt s'affiche journée par journée, motifs bruts compris.
 */
function BlocPennylane({ choisi, ready }: { choisi: string | null; ready: boolean }) {
  const [etat, setEtat] = useState<PennylaneEtat | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [key, setCle] = useState('');
  const [occupe, setOccupe] = useState(false);
  const [rendu, setRendu] = useState<PennylaneDepot['days'] | null>(null);
  // ⚠️ Une clé RÉVOQUÉE doit être remplaçable À L'ÉCRAN : le serveur upserte, mais le champ n'existait
  //    que tant qu'aucune clé n'était posée — la seule sortie était la base (audit 2026-08-24).
  const [remplace, setRemplace] = useState(false);
  const reload = () => {
    api.accountingPennylane().then((e) => { setEtat(e); setFailure(null); })
      .catch((e) => { setEtat(null); setFailure(e instanceof Error ? e.message : String(e)); });
  };
  useEffect(reload, []);

  const put = async () => {
    if (!key.trim() || occupe) return;
    setOccupe(true);
    try {
      const r = await api.accountingPennylaneKey(key.trim());
      showToast(r.detail, 'ok', 6000);
      setCle('');
      setRemplace(false);
      reload();
    } catch (e) {
      showToast(t('Clé refusée : {v}', { v: e instanceof Error ? e.message : String(e) }), 'err', 9000);
    } finally { setOccupe(false); }
  };
  const deposer = async () => {
    if (!choisi || occupe) return;
    setOccupe(true);
    setRendu(null);
    try {
      const r = await api.accountingPennylaneDeposit(choisi);
      setRendu(r.days ?? []);
      const refuses = (r.days ?? []).filter((j) => j.verdict === 'refuse').length;
      showToast(refuses > 0 ? t('Dépôt terminé — {n} journée refusée, détail ci-dessous', { n: refuses }) : t('Dépôt terminé — brouillons chez le comptable'), refuses > 0 ? 'warn' : 'ok', 7000);
    } catch (e) {
      showToast(t('Dépôt refusé : {v}', { v: e instanceof Error ? e.message : String(e) }), 'err', 9000);
    } finally { setOccupe(false); }
  };

  const VERDICTS: Record<string, string> = {
    depose: t('Déposée (brouillon)'), inchange: t('Inchangée — déjà déposée'), correctif_depose: t('Correctif déposé (nouveau brouillon)'), refuse: t('REFUSÉE'),
  };
  return (
    <div className="dcard" style={{ padding: '13px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ fontSize: 'var(--t-title)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('API Pennylane')}</div>
          <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)', lineHeight: 1.6, marginTop: 2 }}>
            <Trans k='Une écriture <1>brouillon par journée</1>, déposée chez le comptable qui valide — rejouable sans doubler (une journée inchangée ne repart pas ; corrigée, elle part en brouillon « correctif » que le cabinet arbitre).' parts={[(x) => <strong>{x}</strong>]} /></div>
          {failure ? <div style={{ fontSize: 'var(--t-body)', color: 'var(--rose)', marginTop: 4 }}>{t('État indisponible : {failure}', { failure })}</div>
            : etat === null ? <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-400)', marginTop: 4 }}>{t('Lecture de l’état…')}</div>
            : !etat.canSeal ? <div style={{ fontSize: 'var(--t-body)', color: 'var(--rose)', marginTop: 4 }}>{t('Chiffrement serveur non configuré (BO_SECRETS_KEY) — aucun secret ne peut être scellé.')}</div>
            : !etat.configured ? <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-600)', marginTop: 4 }}>{t('Aucune clé enregistrée — collez la clé API du compte pour brancher le dépôt.')}</div>
            : <div style={{ fontSize: 'var(--t-body)', color: etat.probe?.ok ? 'var(--teal-800)' : 'var(--rose)', marginTop: 4 }}>{etat.probe?.detail ?? t('Clé enregistrée.')}</div>}
        </div>
        {etat?.canSeal && (!etat.configured || remplace) ? (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              value={key} onChange={(ev) => setCle(ev.target.value)} placeholder={t('Clé API Pennylane')}
              type="password" autoComplete="off" className="field-input" style={{ height: 38, width: 230 }}
            />
            <button type="button" onClick={() => void put()} disabled={!key.trim() || occupe}
              style={{ height: 38, padding: '0 14px', borderRadius: 'var(--r-sm)', border: '1px solid var(--ink-300)', background: 'var(--paper)', fontSize: 'var(--t-lead)', fontWeight: 600, opacity: !key.trim() || occupe ? 0.5 : 1 }}>
              {occupe ? t('Vérification…') : t('Enregistrer')}
            </button>
            {remplace && (
              <button type="button" onClick={() => { setRemplace(false); setCle(''); }}
                style={{ height: 38, padding: '0 10px', borderRadius: 'var(--r-sm)', border: 'none', background: 'transparent', color: 'var(--ink-500)', fontSize: 'var(--t-lead)' }}>
                
                {t('Annuler')}
              </button>
            )}
          </div>
        ) : etat?.configured ? (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button type="button" onClick={() => void deposer()} disabled={!ready || occupe}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 38, padding: '0 14px', borderRadius: 'var(--r-sm)', border: '1px solid var(--ink-300)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: 'var(--t-lead)', fontWeight: 600, cursor: !ready || occupe ? 'default' : 'pointer', opacity: !ready || occupe ? 0.5 : 1 }}>
              <Icon name="upload" size={14} stroke={2} />
              {occupe ? t('Dépôt…') : t('Déposer le mois en brouillons')}
            </button>
            {/* Une clé révoquée/tournée se REMPLACE ici — le serveur sonde puis upserte. */}
            <button type="button" onClick={() => setRemplace(true)}
              style={{ height: 38, padding: '0 10px', borderRadius: 'var(--r-sm)', border: 'none', background: 'transparent', color: 'var(--ink-500)', fontSize: 'var(--t-lead)', textDecoration: 'underline' }}>
              
              {t('Remplacer la clé')}
            </button>
          </div>
        ) : null}
      </div>
      {rendu && rendu.length > 0 && (
        <div style={{ borderTop: '1px solid var(--ink-100)', paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {rendu.map((j) => (
            <div key={j.day} style={{ display: 'flex', gap: 10, fontSize: 'var(--t-body)', alignItems: 'baseline' }}>
              <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink-600)', flexShrink: 0 }}>{j.day}</span>
              <span style={{ fontWeight: 600, color: j.verdict === 'refuse' ? 'var(--rose)' : j.verdict === 'inchange' ? 'var(--ink-500)' : 'var(--teal-800)' }}>{VERDICTS[j.verdict]}</span>
              {j.detail && <span style={{ color: 'var(--ink-500)', wordBreak: 'break-all' }}>{j.detail}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Provenance({ vue }: { vue: EntryView }) {
  // ⚠️ TOUTES les journées : une journée reprise est close par nature, une journée née en caisse ne
  //    l'est que par son scellement — le mois n'est « clos » que si CHACUNE l'est.
  const scelle = vue.days.length > 0 && vue.days.every((d) => d.provenance.closed);
  const extrait = vue.days[0]?.provenance.extractedAt ?? 0;
  const joursOuverts = vue.days.filter((d) => d.provenance.openRegisters.length > 0).length;
  const bloquant = !vue.planComplet || !vue.balanced;

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 12, padding: '13px 16px', borderRadius: 'var(--r-md)',
      background: bloquant ? 'var(--amber-bg)' : scelle ? 'var(--teal-100)' : 'var(--ink-50)',
      border: `1px solid ${bloquant ? 'var(--amber)' : scelle ? 'var(--teal-500)' : 'var(--ink-200)'}`, flexWrap: 'wrap',
    }}>
      <span style={{ color: bloquant ? 'var(--amber-fg)' : scelle ? 'var(--teal-800)' : 'var(--ink-500)', display: 'flex', flexShrink: 0, paddingTop: 1 }}>
        <Icon name={bloquant ? 'alert' : scelle ? 'lock' : 'clock'} size={17} stroke={2.2} />
      </span>
      <span style={{ flex: 1, minWidth: 240, fontSize: 'var(--t-body)', lineHeight: 1.6, color: bloquant ? 'var(--amber-fg)' : 'var(--ink-700)' }}>
        {scelle ? (
          /* ⚠️ « close », pas « scellée par une caisse » : une journée peut être close parce qu'elle est
             REPRISE d'un logiciel qui portait déjà ses clôtures — affirmer une signature de caisse sur
             un historique repris serait faux (décision 2026-08-23). */
          <><Trans k='<1>Période close</1> — chaque journée est figée : scellée par une caisse, ou reprise d’un historique déjà clôturé.' parts={[(x) => <strong>{x}</strong>]} /></>
        ) : (
          <>
            <strong>{t('Période encore ouverte')}</strong>  {t('— aucun grand total mensuel n’est signé.')}
            {joursOuverts > 0 ? <> {t('{n} journée porte une caisse sans clôture.', { n: joursOuverts })}</> : null}
            {extrait ? <> {t('Extraction faite le {v} — des pièces peuvent encore arriver après.', { v: instantLisible(extrait) })}</> : null}
          </>
        )}
        {!vue.planComplet ? (
          <span style={{ display: 'block', marginTop: 5 }}>
            <Trans k='<1>Le plan comptable n’est pas complet</1> : les postes non validés n’ont pas de compte ici. Rien ne peut être remis tant qu’ils ne sont pas renseignés — onglet « Plan comptable ».' parts={[(x) => <strong>{x}</strong>]} /></span>
        ) : null}
        {!vue.balanced ? (
          <span style={{ display: 'block', marginTop: 5 }}>
            <Trans k='<1>Une ou plusieurs journées ne s’équilibrent pas</1> — ouvrez-les ci-dessous : le motif y est écrit, journée par journée.' parts={[(x) => <strong>{x}</strong>]} /></span>
        ) : null}
      </span>
    </div>
  );
}

/** `202607` → `2026-07`, la forme que l'API attend. */
function moisIso(ym: string): string {
  return ym.includes('-') ? ym : `${ym.slice(0, 4)}-${ym.slice(4, 6)}`;
}

/**
 * CE QUE LE SERVEUR DIT DU FICHIER PRODUIT — et ce qu'il ne dit pas.
 *
 * ⚠️ LE COMPTE ARRIVE EN CHAÎNE, dans un en-tête HTTP, et peut manquer. `Intl.PluralRules` choisit
 *    sur un NOMBRE : passer la chaîne telle quelle ferait retomber tous les comptes sur la même
 *    forme (Forme DD). On la LIT, et quand elle ne dit rien, on le DIT — « ? » laissait croire à
 *    un fichier vide ou cassé, alors que c'est seulement l'en-tête qui manque (§23.0).
 */
function lignesDeLEnTete(headers: Headers): number | null {
  const n = Number(headers.get('x-orgazone-lignes'));
  return Number.isInteger(n) && n >= 0 ? n : null;
}

function fecLignes(headers: Headers): string {
  const n = lignesDeLEnTete(headers);
  return n == null ? t('FEC produit — le serveur n’a pas dit combien de lignes') : t('FEC produit — {n} ligne', { n });
}

function fichierLignes(headers: Headers): string {
  const n = lignesDeLEnTete(headers);
  return n == null ? t('Fichier produit — le serveur n’a pas dit combien de lignes') : t('Fichier produit — {n} ligne', { n });
}

/**
 * `202607` → « Juillet 2026 » — DÉRIVÉ d'`Intl`, jamais d'une table écrite à la main.
 *
 * 🔴 UNE TABLE DE DOUZE MOIS VIVAIT ICI, et la migration n'en avait pris que TROIS : `février`,
 *    `août` et `décembre`. Les neuf autres sont des mots d'un seul tenant, en minuscules, sans
 *    accent — indiscernables d'un jeton technique pour le détecteur, qui les écartait (la limite est
 *    écrite dans `i18n-detecteur.mjs`). L'écran aurait donc affiché neuf mois en français et trois
 *    traduits, sans qu'aucun contrôle ne le dise.
 * ⚠️ `Intl` rend le français au même octet près, et TOUTES les langues sans rien écrire de plus.
 */
function moisEnClair(ym: string): string {
  const iso = moisIso(ym);
  const m = Number(iso.slice(5, 7));
  if (!(m >= 1 && m <= 12)) return `${iso.slice(5, 7)} ${iso.slice(0, 4)}`;
  const nom = new Intl.DateTimeFormat(currentLang(), { month: 'long' }).format(new Date(Number(iso.slice(0, 4)), m - 1, 1));
  return `${nom.charAt(0).toUpperCase()}${nom.slice(1)} ${iso.slice(0, 4)}`;
}

/**
 * `20260723` → « Jeudi 23 juillet » — une journée se reconnaît à son jour de semaine.
 * ⚠️ Même dérivation que les mois : la table des sept jours était écrite à la main juste en dessous,
 *    et pas un seul de ses noms n'était passé par le résolveur.
 */
function jourEnClair(ymd: string): string {
  const d = new Date(Number(ymd.slice(0, 4)), Number(ymd.slice(4, 6)) - 1, Number(ymd.slice(6, 8)));
  const rendu = new Intl.DateTimeFormat(currentLang(), { weekday: 'long', day: 'numeric', month: 'long' }).format(d);
  return `${rendu.charAt(0).toUpperCase()}${rendu.slice(1)}`;
}

/** Minutes depuis minuit → `HH:MM`, pour dire la bascule à l'écran. */
function heureDeBascule(minutes: number): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(Math.floor(minutes / 60))}:${p(minutes % 60)}`;
}

/** L'instant d'une extraction, à la minute — la seconde n'apporte rien à qui compare deux exports. */
function instantLisible(epochMs: number): string {
  const d = new Date(epochMs);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} à ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * CE QUI EST DÉJÀ SORTI — la trace, et l'avertissement avant un rejeu.
 *
 * ⚠️ LE COMPTEUR N'EST PAS DÉCORATIF. Le danger d'un export n'est pas chez nous, il est chez le
 * cabinet : deux fichiers du même mois intégrés deux fois font une double écriture. On ne bloque pas
 * pour autant — corriger un compte faux et ré-exporter est le cas d'usage normal — mais on PRÉVIENT.
 *
 * ⚠️ « IDENTIQUE AU PRÉCÉDENT » RÉPOND À LA VRAIE QUESTION du rejeu par doute : quand on ré-exporte
 * après avoir corrigé un compte, on sait que ça change ; quand on ré-exporte parce qu'on a perdu le
 * fichier, savoir que c'est le même évite un appel au cabinet.
 */
function Historique({ month, rafraichir }: { month: string; rafraichir: number }) {
  const [passes, setPasses] = useState<ExportPasse[]>([]);

  useEffect(() => {
    let vivant = true;
    api.accountingExports(month).then((e) => { if (vivant) setPasses(e.exports); }).catch(() => { if (vivant) setPasses([]); });
    return () => { vivant = false; };
  }, [month, rafraichir]);

  if (passes.length === 0) return null;
  const dernier = passes[0]!;

  return (
    <div className="dcard" style={{ overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderBottom: '1px solid var(--ink-200)', flexWrap: 'wrap' }}>
        <span style={{ flex: 1, minWidth: 150, fontSize: 'var(--t-strong)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Exports déjà produits')}</span>
        <span style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)', flexShrink: 0 }}>
          {t('{n} fichier produit', { n: passes.length })}
        </span>
      </div>

      <div style={{ padding: '12px 18px', background: 'var(--amber-bg)', fontSize: 'var(--t-body)', color: 'var(--amber-fg)', lineHeight: 1.6, borderBottom: '1px solid var(--ink-100)' }}>
        <strong>{t('Ce mois a déjà été exporté {n} fois', { n: passes.length })}</strong>  {dernier.exportedBy
          ? t('— dernier le {date} par {exportedBy} au format {format}.', { date: instantLisible(dernier.exportedAt), exportedBy: dernier.exportedBy, format: dernier.formatLabel })
          : t('— dernier le {date} au format {format}.', { date: instantLisible(dernier.exportedAt), format: dernier.formatLabel })}
        {' '}{t('Le refaire est possible — c’est ce qu’on fait après avoir corrigé un compte — mais deux fichiers du même mois intégrés deux fois font une double écriture chez le cabinet.')}
      </div>

      {passes.map((p, i) => (
        <div key={`${p.exportedAt}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 18px', fontSize: 'var(--t-lead)', flexWrap: 'wrap', borderBottom: '1px solid var(--ink-100)' }}>
          <span style={{ fontWeight: 600, color: 'var(--ink-900)', minWidth: 150, whiteSpace: 'nowrap' }}>{instantLisible(p.exportedAt)}</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 9px', borderRadius: 'var(--r-xs)', background: 'var(--ink-100)', color: 'var(--ink-700)', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-sub)', fontWeight: 700, whiteSpace: 'nowrap' }}>
            {p.formatCode}
          </span>
          <span style={{ flex: 1, minWidth: 120, fontSize: 'var(--t-body)', color: 'var(--ink-600)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {p.exportedBy || '—'} · {t('{n} ligne', { n: p.lineCount })} · {p.periodClosed ? t('période scellée') : t('période ouverte')}
          </span>
          {/* ⚠️ L'EMPREINTE RÉPOND À « EST-CE LE MÊME FICHIER ? », et à rien d'autre : deux lignes qui la
              partagent ont produit le même contenu comptable, quel que soit l'instant. */}
          {i + 1 < passes.length && p.fingerprint === passes[i + 1]!.fingerprint ? (
            <span style={{ fontSize: 'var(--t-note)', fontWeight: 700, color: 'var(--teal-800)', whiteSpace: 'nowrap' }}>{t('identique au précédent')}</span>
          ) : null}
        </div>
      ))}

      <div style={{ padding: '14px 18px', background: 'var(--ink-50)', fontSize: 'var(--t-body)', color: 'var(--ink-600)', lineHeight: 1.6 }}>
        <Trans k='<1>Un mois inchangé s’exporte à l’identique</1> — l’empreinte le dit. Mais corriger un compte au plan comptable CHANGE le fichier, même sur un mois scellé : les faits sont figés, la correspondance vers les comptes ne l’est pas.' parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} /></div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// LES FORMATS PARAMÉTRÉS
// ---------------------------------------------------------------------------

/**
 * LES FORMATS D'EXPORT — ce que chacun produit, sur les écritures réelles du magasin.
 *
 * ⚠️ L'APERÇU TOURNE DANS LE NAVIGATEUR AVEC LE CODE DU SERVEUR. `@orgazone/core-accounting` est un
 * cœur pur : l'écran l'appelle directement sur les journées déjà chargées. Ce n'est pas une
 * commodité — c'est ce qui garantit que l'aperçu et le fichier téléchargé ne peuvent PAS diverger,
 * là où un aperçu réimplémenté à l'écran finit toujours par mentir d'un séparateur ou d'une décimale.
 *
 * ⚠️ L'APERÇU EST FAIT SUR DE VRAIES ÉCRITURES, jamais sur un exemple inventé : un format se juge sur
 * ses propres comptes et ses propres montants, pas sur « Dupont 123,45 » (§23.0).
 */
function Formats() {
  const scope = useStoreScope();
  const gln = scope.view?.active?.gln ?? null;
  const [formats, setFormats] = useState<FormatDisponible[] | null>(null);
  const [choisi, setChoisi] = useState<string | null>(null);
  const [month, setMois] = useState<string | null>(null);
  const [vue, setVue] = useState<EntryView | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [edition, setEdition] = useState<Edition | null>(null);
  const [envoi, setEnvoi] = useState(false);

  const load = useCallback(() => {
    if (!gln) return;
    api.accountingFormats().then((f) => setFormats(f.formats)).catch(() => undefined);
  }, [gln]);

  useEffect(() => {
    if (!gln) return;
    let vivant = true;
    Promise.all([api.accountingFormats(), api.cashMonths()])
      .then(([f, m]) => {
        if (!vivant) return;
        setFormats(f.formats);
        setChoisi((c) => c ?? f.formats[0]?.code ?? null);
        setMois(m.months[0] ? moisIso(m.months[0].ym) : null);
      })
      .catch((e: unknown) => { if (vivant) setErreur(e instanceof Error ? e.message : String(e)); });
    return () => { vivant = false; };
  }, [gln]);

  useEffect(() => {
    if (!month) return;
    let vivant = true;
    api.accountingEntry(month).then((v) => { if (vivant) setVue(v); }).catch(() => { if (vivant) setVue(null); });
    return () => { vivant = false; };
  }, [month]);

  const enregistre = formats?.find((f) => f.code === choisi) ?? null;
  // ⚠️ L'APERÇU SUIT CE QU'ON TAPE, pas ce qui est enregistré : un créateur de format qui montrerait
  //    l'ancien fichier pendant qu'on règle le nouveau ne servirait à rien.
  // ⚠️ MÉMOÏSÉ : l'objet fabriqué pour l'édition serait NEUF à chaque rendu, donc l'aperçu se
  //    recalculerait à chaque frappe de touche — sur un mois entier d'écritures.
  const format: FormatDisponible | null = useMemo(() => (edition
    ? { code: edition.code, label: edition.label, spec: edition.spec, livre: false, sourceGln: null, sourceAt: null, illisible: null }
    : enregistre), [edition, enregistre]);
  const defautsEdition = useMemo(() => (edition ? defautsDuFormat(edition.spec).map((d) => d.message) : []), [edition]);

  /**
   * L'aperçu — les cinq premières lignes du fichier que ce format produirait.
   *
   * ⚠️ IL PASSE PAR `forDelivery`, comme le vrai export : si le plan est incomplet ou une journée
   * déséquilibrée, l'aperçu DIT le refus au lieu de montrer un fichier qu'on ne pourra pas produire.
   */
  const apercu = useMemo(() => {
    if (!format || !vue) return null;
    const porte = forDelivery(vue.days, vue.planComplet);
    if (!porte.ok) return { refusal: porte.refusal.map((r) => r.message), texte: '', defauts: [] as string[] };
    const f = formatEntries(porte.entries, format.spec);
    return {
      refusal: [],
      texte: f.texte.split(format.spec.finDeLigne).slice(0, 6).join('\n'),
      defauts: f.defauts.map((d) => d.message),
    };
  }, [format, vue]);

  /**
   * Ouvre le formulaire sur une COPIE des réglages d'un format existant.
   * ⚠️ Dupliquer est le seul moyen de partir du format livré : il vit dans le code, donc il ne se
   * modifie pas — un réglage posé dessus disparaîtrait à la livraison suivante.
   */
  const dupliquer = (f: FormatDisponible) =>
    setEdition({ creation: true, code: '', label: t('{label} (copie)', { label: f.label }), spec: { ...f.spec, colonnes: f.spec.colonnes.map((c) => ({ ...c })) } });

  const modifier = (f: FormatDisponible) =>
    setEdition({ creation: false, code: f.code, label: f.label, spec: { ...f.spec, colonnes: f.spec.colonnes.map((c) => ({ ...c })) } });

  const save = async () => {
    if (!edition || envoi) return;
    setEnvoi(true);
    try {
      // ⚠️ `nom` suit le libellé : c'est lui qui apparaît dans les réglages relus, et deux noms pour un
      //    même format finiraient par désigner deux choses dans l'esprit du lecteur.
      const spec = { ...edition.spec, nom: edition.label.trim() };
      await api.writeMasterData(
        'accounting_format',
        [{ code: edition.code.trim(), label: edition.label.trim(), spec: JSON.stringify(spec) }],
        // ⚠️ CLÉ D’IDEMPOTENCE (voir plus haut) — elle sort vers le serveur.
        `format-compta:${edition.code.trim()}`,
      );
      showToast(t('Format « {v} » enregistré', { v: edition.label.trim() }), 'info', 4000);
      setChoisi(edition.code.trim());
      setEdition(null);
      load();
    } catch (err) {
      // ⚠️ Le motif vient du serveur : il désigne le réglage fautif, là où « échec » obligerait à relire
      //    onze champs à l'œil.
      showToast(t('Format refusé : {v}', { v: err instanceof Error ? err.message : String(err) }), 'err', 9000);
    } finally {
      setEnvoi(false);
    }
  };

  const remove = async (f: FormatDisponible) => {
    // ⚠️ LES EXPORTS PASSÉS NE PERDENT RIEN : leur trace garde le code ET le libellé du format sous
    //    forme de texte. Supprimer un format n'efface donc aucun historique.
    const ok = await confirm({
      title: t('Supprimer le format « {label} » ?', { label: f.label }),
      message: t('Les exports déjà produits gardent sa trace. Seul le format disparaît de la liste.'),
      confirmLabel: t('Supprimer'),
    });
    if (!ok) return;
    try {
      await api.deleteReferentielEntry('accounting_format', { code: f.code }, `format-compta-suppr:${f.code}`);
      showToast(t('Format « {label} » supprimé', { label: f.label }), 'info', 4000);
      if (choisi === f.code) setChoisi(null);
      load();
    } catch (err) {
      showToast(t('Suppression refusée : {v}', { v: err instanceof Error ? err.message : String(err) }), 'err', 9000);
    }
  };

  if (!gln) return <EmptyState icon="store" title={t('Aucun magasin actif')} note={t('Choisissez le magasin à administrer pour voir ses formats.')} />;
  if (erreur) return <EmptyState icon="alert" title={t('Formats illisibles')} note={erreur} />;
  if (!formats) return <EmptyState icon="clock" title={t('Lecture des formats…')} note={t('Les formats de ce magasin sont en cours de lecture.')} />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="dcard" style={{ overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderBottom: '1px solid var(--ink-200)', flexWrap: 'wrap' }}>
          <span style={{ flex: 1, minWidth: 150, fontSize: 'var(--t-strong)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Formats disponibles')}</span>
          <button
            type="button" className="oz-integ-btn" style={boutonSecondaire}
            onClick={() => dupliquer(formats[0]!)}
          >
            
            {t('Nouveau format')}
          </button>
        </div>
        {formats.map((f) => (
          <button aria-pressed={choisi === f.code}
            key={f.code} type="button" onClick={() => setChoisi(f.code)} className="oz-integ-ligne"
            style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px', width: '100%',
              border: 'none', borderBottom: '1px solid var(--ink-100)', textAlign: 'left', font: 'inherit',
              background: choisi === f.code ? 'var(--teal-100)' : 'transparent', cursor: 'pointer', flexWrap: 'wrap',
            }}
          >
            <span style={{ flex: 1, minWidth: 160 }}>
              <span style={{ display: 'block', fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-900)' }}>{f.label}</span>
              <span style={{ display: 'block', fontSize: 'var(--t-note)', color: 'var(--ink-500)', marginTop: 2, fontFamily: 'var(--font-mono)' }}>{f.code}</span>
            </span>
            {f.livre ? (
              <span style={{ height: 22, display: 'inline-flex', alignItems: 'center', padding: '0 9px', borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', color: 'var(--ink-600)', fontSize: 'var(--t-meta)', fontWeight: 700 }}>
                
                {t('Livré')}
              </span>
            ) : null}
            {/* ⚠️ LA REPRISE SE DIT : un format venu d'un autre magasin est une COPIE datée, pas un
                héritage — savoir d'où il vient est ce qui permet de décider de le reprendre à nouveau. */}
            {f.sourceGln ? (
              <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-400)' }}>{t('Repris de {sourceGln}', { sourceGln: f.sourceGln })}</span>
            ) : null}
            {f.illisible ? (
              <span style={{ fontSize: 'var(--t-note)', color: 'var(--rose-fg)', fontWeight: 700 }}>{t('Inexploitable : {illisible}', { illisible: f.illisible })}</span>
            ) : null}
            {/* ⚠️ `<span role="button">` et non `<button>` : la rangée EST déjà un bouton, et un bouton
                imbriqué dans un bouton est un HTML invalide que les navigateurs réparent chacun à leur
                manière — le clic finit par atteindre le mauvais des deux. */}
            <span style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              <span
                role="button" tabIndex={0} className="oz-integ-btn" style={boutonSecondaire}
                onClick={(ev) => { ev.stopPropagation(); dupliquer(f); }}
                onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); ev.stopPropagation(); dupliquer(f); } }}
              >
                
                {t('Dupliquer')}
              </span>
              {!f.livre ? (
                <>
                  <span
                    role="button" tabIndex={0} className="oz-integ-btn" style={boutonSecondaire}
                    onClick={(ev) => { ev.stopPropagation(); modifier(f); }}
                    onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); ev.stopPropagation(); modifier(f); } }}
                  >
                    
                    {t('Modifier')}
                  </span>
                  <span
                    role="button" tabIndex={0} className="oz-integ-btn" style={boutonSecondaire}
                    onClick={(ev) => { ev.stopPropagation(); void remove(f); }}
                    onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); ev.stopPropagation(); void remove(f); } }}
                  >
                    
                    {t('Supprimer')}
                  </span>
                </>
              ) : null}
            </span>
          </button>
        ))}
      </div>

      {edition ? (
        <EditeurFormat
          e={edition} defauts={defautsEdition} envoi={envoi}
          onChange={setEdition} onSave={() => void save()} onCancel={() => setEdition(null)}
        />
      ) : null}

      {format ? (
        <div className="dcard" style={{ overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderBottom: '1px solid var(--ink-200)', flexWrap: 'wrap' }}>
            <span style={{ flex: 1, minWidth: 150, fontSize: 'var(--t-strong)', fontWeight: 700, color: 'var(--ink-900)' }}>
              {edition ? t('Aperçu de ce que vous réglez') : t('Réglages')}
            </span>
            <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-400)' }}>{format.label}</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '14px 18px' }}>
            {reglagesLisibles(format.spec).map((r) => (
              <span key={r} style={{ height: 24, display: 'inline-flex', alignItems: 'center', padding: '0 10px', borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', color: 'var(--ink-700)', fontSize: 'var(--t-note)', fontWeight: 600 }}>
                {r}
              </span>
            ))}
          </div>
          <div style={{ padding: '0 18px 14px', fontSize: 'var(--t-dense)', color: 'var(--ink-500)', lineHeight: 1.55 }}>
            {t('Colonnes : {v}', { v: format.spec.colonnes.map((c) => c.heading).join(' · ') })}
          </div>

          <div style={{ padding: '13px 18px', borderTop: '1px solid var(--ink-200)', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>
            
            {t('Aperçu')} {month ? `— ${moisEnClair(month)}` : ''}
          </div>
          {apercu === null ? (
            <div style={{ padding: '14px 18px', fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>{t('Lecture des écritures…')}</div>
          ) : apercu.refusal.length > 0 ? (
            <div style={{ padding: '14px 18px', fontSize: 'var(--t-body)', color: 'var(--amber-fg)', background: 'var(--amber-bg)', lineHeight: 1.6 }}>
              {apercu.refusal.map((m) => <div key={m}>{m}</div>)}
            </div>
          ) : (
            <>
              {apercu.defauts.map((m) => (
                <div key={m} style={{ padding: '10px 18px', fontSize: 'var(--t-dense)', color: 'var(--rose-fg)', background: 'var(--rose-bg)', lineHeight: 1.5 }}>{m}</div>
              ))}
              <pre style={{ margin: 0, padding: '13px 18px', background: 'var(--ink-950)', color: '#d4f7ec', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-note)', lineHeight: 1.7, overflowX: 'auto' }}>
                {apercu.texte}
              </pre>
            </>
          )}
        </div>
      ) : null}

      <div style={{ padding: '13px 16px', borderRadius: 'var(--r-md)', background: 'var(--ink-50)', border: '1px solid var(--ink-200)', fontSize: 'var(--t-body)', color: 'var(--ink-600)', lineHeight: 1.6 }}>
        <Trans k='<1>Le format livré ne se modifie pas</1> — il vit dans le code, et un réglage posé dessus disparaîtrait à la livraison suivante. Dupliquez-le pour partir de ses valeurs. Reste à construire : la <2>reprise depuis un autre magasin</2> du même tenant — une copie datée, jamais un héritage vivant.' parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} /></div>
    </div>
  );
}

/** Les réglages d'un format, dits en clair — ce sont eux qu'on relit pour comprendre un fichier. */
function reglagesLisibles(spec: FormatSpec): string[] {
  const sep = spec.separateur === 'fixed' ? t('Largeur fixe')
    : spec.separateur === '\t' ? t('Séparateur : tabulation')
      : t('Séparateur : « {separateur} »', { separateur: spec.separateur });
  const enc = spec.encodage === 'utf-8-bom' ? t('UTF-8 avec marque d’ordre')
    : spec.encodage === 'windows-1252' ? t('Windows-1252') : 'UTF-8';
  const sens = spec.sens === 'colonnes' ? t('Débit / Crédit en deux colonnes')
    : spec.sens === 'signe' ? t('Montant signé') : t('Montant + sens D/C');
  return [
    sep, enc, `.${spec.extension}`, t('Date : {masqueDate}', { masqueDate: spec.masqueDate }),
    t('Décimale : « {decimale} »', { decimale: spec.decimale }), sens,
    spec.enTete ? t('Avec ligne d’en-tête') : t('Sans ligne d’en-tête'),
    spec.finDeLigne === '\r\n' ? t('Fin de ligne CRLF') : t('Fin de ligne LF'),
  ];
}

// ---------------------------------------------------------------------------
// LE CRÉATEUR DE FORMAT
// ---------------------------------------------------------------------------

/** Un format en cours d'écriture — tant qu'il n'est pas enregistré, il n'existe qu'ici. */
interface Edition { creation: boolean; code: string; label: string; spec: FormatSpec }

const CHAMPS: readonly { v: ChampExport; l: Translatable }[] = [
  { v: 'date', l: key('Date') }, { v: 'journal', l: key('Journal') }, { v: 'compte', l: key('Compte') },
  { v: 'libelle', l: key('Libellé') }, { v: 'debit', l: key('Débit') }, { v: 'credit', l: key('Crédit') },
  { v: 'montant', l: key('Montant') }, { v: 'sens', l: key('Sens (D/C)') }, { v: 'devise', l: key('Devise') },
];



/**
 * LE FORMULAIRE D'UN FORMAT — chaque réglage, et ce qu'il produit, côte à côte.
 *
 * ⚠️ LES DÉFAUTS S'AFFICHENT PENDANT LA SAISIE, pas à l'enregistrement. Un format incohérent produit un
 * fichier BIEN FORMÉ et faux — colonne Crédit oubliée, séparateur décimal égal au délimiteur : le
 * cabinet ne s'en aperçoit qu'après intégration. Les montrer au moment où on les crée est la seule
 * occasion de les corriger sans coût.
 *
 * ⚠️ LE CODE NE SE MODIFIE PAS APRÈS CRÉATION : c'est l'identité de la ligne. Le changer créerait un
 * second format au lieu de renommer le premier — et l'ancien resterait, invisible, dans la liste.
 */
function EditeurFormat({ e, defauts, onChange, onSave, onCancel, envoi }: {
  e: Edition; defauts: string[]; onChange: (e: Edition) => void;
  onSave: () => void; onCancel: () => void; envoi: boolean;
}) {
  const maj = (spec: Partial<FormatSpec>) => onChange({ ...e, spec: { ...e.spec, ...spec } });
  const majColonne = (i: number, c: Partial<ColonneSpec>) =>
    maj({ colonnes: e.spec.colonnes.map((x, k) => (k === i ? { ...x, ...c } : x)) });
  const deplacer = (i: number, sens: -1 | 1) => {
    const j = i + sens;
    if (j < 0 || j >= e.spec.colonnes.length) return;
    const cols = [...e.spec.colonnes];
    [cols[i], cols[j]] = [cols[j]!, cols[i]!];
    maj({ colonnes: cols });
  };

  return (
    <div className="dcard" style={{ overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderBottom: '1px solid var(--ink-200)', flexWrap: 'wrap' }}>
        <span style={{ flex: 1, minWidth: 150, fontSize: 'var(--t-strong)', fontWeight: 700, color: 'var(--ink-900)' }}>
          {e.creation ? t('Nouveau format') : t('Modifier « {label} »', { label: e.label })}
        </span>
        <button type="button" onClick={onCancel} className="oz-integ-btn" style={boutonSecondaire}>{t('Annuler')}</button>
        <button
          type="button" onClick={onSave} disabled={envoi || defauts.length > 0} className="oz-integ-btn"
          style={{
            ...boutonPrincipal,
            cursor: envoi || defauts.length > 0 ? 'not-allowed' : 'pointer',
            opacity: envoi || defauts.length > 0 ? 0.5 : 1,
          }}
        >
          {envoi ? t('Enregistrement…') : t('Enregistrer le format')}
        </button>
      </div>

      {defauts.length > 0 ? (
        <div style={{ padding: '11px 18px', background: 'var(--amber-bg)', color: 'var(--amber-fg)', fontSize: 'var(--t-body)', lineHeight: 1.6 }}>
          {defauts.map((d) => <div key={d}>{d}</div>)}
        </div>
      ) : null}

      <div className="oz-fmt-grille" style={{ padding: '14px 18px' }}>
        <FormRow label={t('Code')}>
          {/* ⚠️ Verrouillé après création : c'est l'identité de la ligne. */}
          <input value={e.code} disabled={!e.creation} onChange={(x) => onChange({ ...e, code: x.target.value })}
            placeholder="CEGID" style={{ ...fieldStyle, opacity: e.creation ? 1 : 0.6, fontFamily: 'var(--font-mono)' }} />
        </FormRow>
        <FormRow label={t('Nom affiché')}>
          <input value={e.label} onChange={(x) => onChange({ ...e, label: x.target.value })}
            placeholder={t('Cegid Expert')} style={fieldStyle} />
        </FormRow>
        <FormRow label={t('Séparateur')}>
          <select value={e.spec.separateur} onChange={(x) => maj({ separateur: x.target.value as FormatSpec['separateur'] })} style={fieldStyle}>
            <option value=";">{t('Point-virgule ;')}</option>
            <option value=",">{t('Virgule ,')}</option>
            <option value="&#9;">{t('Tabulation')}</option>
            <option value="|">{t('Barre verticale |')}</option>
            <option value="fixed">{t('Largeur fixe')}</option>
          </select>
        </FormRow>
        <FormRow label={t('Encodage')}>
          <select value={e.spec.encodage} onChange={(x) => maj({ encodage: x.target.value as FormatSpec['encodage'] })} style={fieldStyle}>
            <option value="utf-8-bom">{t('UTF-8 avec marque d’ordre')}</option>
            <option value="utf-8">{t('UTF-8 nu')}</option>
            <option value="windows-1252">{t('Windows-1252')}</option>
          </select>
        </FormRow>
        <FormRow label={t('Extension')}>
          <input value={e.spec.extension} onChange={(x) => maj({ extension: x.target.value.replace(/[^a-z0-9]/gi, '') })}
            placeholder="csv" style={{ ...fieldStyle, fontFamily: 'var(--font-mono)' }} />
        </FormRow>
        <FormRow label={t('Masque de date')}>
          <input value={e.spec.masqueDate} onChange={(x) => maj({ masqueDate: x.target.value })}
            list="oz-masques-date" placeholder={t('JJ/MM/AAAA')} style={{ ...fieldStyle, fontFamily: 'var(--font-mono)' }} />
          <datalist id="oz-masques-date">{DATE_MASK_SUGGESTIONS.map((m) => <option key={m} value={m} />)}</datalist>
        </FormRow>
        <FormRow label={t('Séparateur décimal')}>
          <select value={e.spec.decimale} onChange={(x) => maj({ decimale: x.target.value as ',' | '.' })} style={fieldStyle}>
            <option value=",">{t('Virgule ,')}</option>
            <option value=".">{t('Point .')}</option>
          </select>
        </FormRow>
        <FormRow label={t('Sens de la ligne')}>
          <select value={e.spec.sens} onChange={(x) => maj({ sens: x.target.value as ConventionSens })} style={fieldStyle}>
            <option value="colonnes">{t('Débit / Crédit en deux colonnes')}</option>
            <option value="signe">{t('Montant signé (crédit négatif)')}</option>
            <option value="lettre">{t('Montant + colonne D/C')}</option>
          </select>
        </FormRow>
        <FormRow label={t('Fin de ligne')}>
          <select value={e.spec.finDeLigne} onChange={(x) => maj({ finDeLigne: x.target.value as '\r\n' | '\n' })} style={fieldStyle}>
            <option value="&#13;&#10;">{t('CRLF (Windows)')}</option>
            <option value="&#10;">LF</option>
          </select>
        </FormRow>
        <FormRow label={t('Ligne d’en-tête')}>
          <select value={e.spec.enTete ? 'oui' : 'non'} onChange={(x) => maj({ enTete: x.target.value === 'oui' })} style={fieldStyle}>
            <option value="oui">{t('Avec')}</option>
            <option value="non">{t('Sans')}</option>
          </select>
        </FormRow>
      </div>

      <div style={{ padding: '0 18px 6px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ flex: 1, minWidth: 120, fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>
          
          {t('Colonnes, dans l’ordre du fichier')}
        </span>
        <button
          type="button" className="oz-integ-btn" style={boutonSecondaire}
          onClick={() => maj({ colonnes: [...e.spec.colonnes, { champ: 'libelle', heading: t('Libellé') }] })}
        >
          
          {t('Ajouter une colonne')}
        </button>
      </div>

      {e.spec.colonnes.map((c, i) => (
        <div key={`${c.champ}-${i}`} className="oz-fmt-colonne" style={{ padding: '7px 18px', alignItems: 'center' }}>
          <select aria-label={t('Champ de la colonne {v}', { v: i + 1 })} value={c.champ} onChange={(x) => majColonne(i, { champ: x.target.value as ChampExport })} style={fieldStyle}>
            {CHAMPS.map((f) => <option key={f.v} value={f.v}>{t(f.l)}</option>)}
          </select>
          <input aria-label={t('En-tête de la colonne {v}', { v: i + 1 })} value={c.heading} onChange={(x) => majColonne(i, { heading: x.target.value })} placeholder={t('En-tête')} style={fieldStyle} />
          {/* ⚠️ La largeur n'apparaît QU'EN largeur fixe : ailleurs elle n'a aucun effet, et un champ
              sans effet se lit comme un réglage qu'on aurait oublié d'appliquer. */}
          {e.spec.separateur === 'fixed' ? (
            <input type="number" min={1} aria-label={t('Largeur de la colonne {v}', { v: i + 1 })} value={c.largeur ?? ''} placeholder={t('largeur')}
              onChange={(x) => majColonne(i, { largeur: Number(x.target.value) || undefined })}
              style={{ ...fieldStyle, fontFamily: 'var(--font-mono)' }} />
          ) : <span />}
          <span style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
            <button type="button" onClick={() => deplacer(i, -1)} aria-label={t('Monter la colonne {v}', { v: i + 1 })} className="oz-integ-btn" style={boutonIcone}>
              <Icon name="chevronU" size={13} stroke={2.2} />
            </button>
            <button type="button" onClick={() => deplacer(i, 1)} aria-label={t('Descendre la colonne {v}', { v: i + 1 })} className="oz-integ-btn" style={boutonIcone}>
              <Icon name="chevronD" size={13} stroke={2.2} />
            </button>
            <button
              type="button" aria-label={t('Retirer la colonne {v}', { v: i + 1 })} className="oz-integ-btn" style={boutonIcone}
              onClick={() => maj({ colonnes: e.spec.colonnes.filter((_, k) => k !== i) })}
            >
              <Icon name="trash" size={13} stroke={2.2} />
            </button>
          </span>
        </div>
      ))}
      <div style={{ height: 12 }} />
    </div>
  );
}

/** Un réglage : son nom au-dessus, son contrôle en dessous. */
function FormRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>{label}</span>
      {children}
    </label>
  );
}

const fieldStyle: React.CSSProperties = {
  height: 36, padding: '0 10px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)',
  background: 'var(--paper)', color: 'var(--ink-900)', fontSize: 'var(--t-body)', width: '100%', fontFamily: 'inherit',
};

const boutonSecondaire: React.CSSProperties = {
  height: 34, padding: '0 12px', border: '1px solid var(--ink-300)', borderRadius: 'var(--r-ctrl)',
  background: 'var(--paper)', color: 'var(--ink-800)', fontSize: 'var(--t-body)', fontWeight: 600,
  cursor: 'pointer', font: 'inherit', fontFamily: 'inherit', whiteSpace: 'nowrap',
};

const boutonPrincipal: React.CSSProperties = {
  height: 34, padding: '0 14px', border: 'none', borderRadius: 'var(--r-ctrl)',
  background: 'var(--teal-500)', color: '#fff', fontSize: 'var(--t-body)', fontWeight: 700,
  font: 'inherit', fontFamily: 'inherit', whiteSpace: 'nowrap',
};

const boutonIcone: React.CSSProperties = {
  width: 30, height: 30, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)',
  background: 'var(--paper)', color: 'var(--ink-600)', display: 'flex', alignItems: 'center',
  justifyContent: 'center', cursor: 'pointer', padding: 0,
};

/** Étiquette d'un filtre — relevé maquette l.10317 : 11px/600, `ink-500`, 5px sous le libellé. */
function EtiquetteFiltre({ children }: { children: React.ReactNode }) {
  return (
    <span style={{ display: 'block', fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--ink-500)', marginBottom: 5 }}>
      {children}
    </span>
  );
}

/** Liste déroulante de la barre de filtres — relevé maquette l.10325 (h42, r-sm, 13.5px/600). */
const selectFiltre: React.CSSProperties = {
  width: '100%', height: 42, padding: '0 12px', border: '1px solid var(--ink-200)',
  borderRadius: 'var(--r-sm)', background: 'var(--paper)', color: 'var(--ink-900)',
  fontSize: 'var(--t-title)', fontWeight: 600, outline: 'none', fontFamily: 'inherit',
};

/**
 * LE CANAL DE REMISE — fichier, ou API comptable.
 *
 * ⚠️ L'API EST MONTRÉE MAIS INERTE, et le dit. La maquette dessine deux canaux ; un seul existe. Le
 * cacher laisserait croire que le fichier est la seule voie prévue ; l'offrir sans rien derrière serait
 * l'interdit n°6 du §23.0. Il est donc désactivé, avec son motif — même traitement que les connecteurs
 * « à venir » de l'écran Intégrations.
 */
function Canal() {
  return (
    <span style={{ display: 'inline-flex', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', overflow: 'hidden' }}>
      <span style={{
        height: 40, padding: '0 14px', background: 'var(--teal-500)', color: '#fff',
        fontSize: 'var(--t-lead)', fontWeight: 700, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 7,
      }}>
        <Icon name="download" size={14} stroke={2} />
        
        {t('Fichier')}
      </span>
      <Tooltip label={t('Par le bloc dédié')} content={t('Le dépôt API (Pennylane, brouillons par journée) se fait par le bloc « API Pennylane » plus bas sur cet écran — ce sélecteur ne concerne que les fichiers.')}>
        <span style={{
          height: 40, padding: '0 14px', background: 'var(--paper)', color: 'var(--ink-400)',
          fontSize: 'var(--t-lead)', fontWeight: 700, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 7,
          cursor: 'not-allowed',
        }}>
          <Icon name="api" size={14} stroke={2} />
          
          {t('API comptable')}
        </span>
      </Tooltip>
    </span>
  );
}
